import { tabbedDataGridPage } from "../../styles/commonPageStyles";

// Portal-style layout: white header block with title + Reload, a compact
// filter row, then the standard portal table filling the remaining height.
export const packageVulnerabilityStyles = {
  mainBox: {
    ...tabbedDataGridPage.tabContentMainBox,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  headerGrid: { ...tabbedDataGridPage.gridContainer, flexShrink: 0 },
  filterStack: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "6px",
    mb: "8px",
    flexShrink: 0,
  },
  // Wider triggers for filters holding long values (CVE IDs, Maven
  // coordinates, repository names); others size to their label.
  filterButton: (filterName) => {
    if (/vulnerability|package|repo/i.test(filterName)) {
      return { minWidth: "220px", justifyContent: "space-between" };
    }
    return {};
  },
  // "{filtered} of {total} findings" on the right of the filter bar.
  findingsCount: {
    ml: "auto",
    fontSize: "12px",
    color: "text.secondary",
    whiteSpace: "nowrap",
  },
  // Applied-filters row above the table; hidden when nothing is selected.
  appliedFiltersRow: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "6px",
    mb: "12px",
    flexShrink: 0,
  },
  appliedFiltersLabel: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: "text.secondary",
  },
  // Soft blue tag chips (portal style); full ID always visible.
  appliedFilterChip: (colors) => ({
    height: "22px",
    borderRadius: "4px",
    bgcolor: colors.qaBg,
    color: colors.qaFg,
    fontSize: "11px",
    fontWeight: 600,
    maxWidth: "none",
    "& .MuiChip-label": { overflow: "visible", textOverflow: "clip" },
    "& .MuiChip-deleteIcon": {
      fontSize: "14px",
      color: colors.infoFg,
      "&:hover": { color: colors.qaFg },
    },
  }),
  clearAllButton: {
    ml: "auto",
    textTransform: "none",
    fontSize: "12px",
    fontWeight: 600,
    minWidth: 0,
    p: "2px 8px",
  },
  // Portal Fix column: 14px check (success) / cross (danger).
  fixAvailableIconStyles: (colors, fixAvailable) => ({
    fontSize: 16,
    color: fixAvailable ? colors.okText : colors.errText,
  }),
  dataTable: { flex: 1, minHeight: 0 },
};

// Portal side drawer: fixed 480px right panel, grey header, scrollable body
// with badge row, uppercase section labels, mono details block and a
// timeline-style approval history.
export const vulnerabilityDrawerStyles = {
  paper: (colors, width = 720) => ({
    width: `min(${width}px, 95vw)`,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.primary[700],
  }),
  header: (colors) => ({
    px: 3,
    py: 2,
    backgroundColor: colors.grey[900],
    borderBottom: `1px solid ${colors.grey[800]}`,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 2,
    flexShrink: 0,
  }),
  title: (colors) => ({
    fontSize: "15px",
    fontWeight: 700,
    color: colors.grey[100],
  }),
  subtitle: (colors) => ({
    fontSize: "12px",
    color: colors.grey[400],
  }),
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    p: "20px 24px",
  },
  badgeRow: { display: "flex", gap: "6px", mb: "20px" },
  sectionLabel: (colors) => ({
    fontSize: "11px",
    fontWeight: 600,
    color: colors.grey[300],
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    mb: "8px",
  }),
  metaBlock: (colors) => ({
    background: colors.primary[600],
    border: `1px solid ${colors.grey[800]}`,
    borderRadius: "6px",
    p: "14px",
    fontSize: "12px",
    fontFamily: "ui-monospace,'SF Mono',Menlo,monospace",
    lineHeight: 1.9,
    color: colors.grey[700],
    mb: "20px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  }),
  historyList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    mb: "20px",
  },
  historyItem: { display: "flex", gap: "12px", alignItems: "flex-start" },
  historyIcon: ({ bg, border, fg }) => ({
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    background: bg,
    border: `1.5px solid ${border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: fg,
  }),
  historyName: (colors) => ({
    fontSize: "13px",
    fontWeight: 600,
    color: colors.grey[700],
  }),
  historyDate: (colors) => ({
    fontSize: "11px",
    color: colors.grey[300],
    mt: "2px",
  }),
};
